import React, { useMemo, useState } from 'react'
import {
  Box,
  Spinner,
  Center,
  Heading,
  Flex,
  Button,
  HStack,
  Text,
  useColorModeValue,

} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import DataTable from '../components/AllDataTable'
import { dataService } from '../services/api'
import { capitalize } from '../utils/helpers'

const DataPage: React.FC = () => {
  const param = useParams()

  // Get recordType from URL or default to 'Data'
  const recordType = param.type || 'Data'

  const [activeFilter, setActiveFilter] = useState<'active' | 'inactive' | 'all'>('active')
  const filterBg = useColorModeValue('white', 'gray.800')
  const filterBorderColor = useColorModeValue('gray.200', 'gray.700')
  const filterLabelColor = useColorModeValue('gray.600', 'gray.300')

  const { data, isLoading, error } = useQuery({
    queryKey: ['dataRecords', recordType],
    queryFn: async () => {
      // @ts-ignore
      const response = await dataService.getAllDataRecords(recordType)
      return response.data || []
    },
    select: (data) => {
      return [...data].sort((a, b) => 
        new Date(b.record.last_updated).getTime() - new Date(a.record.last_updated).getTime()
      )
    }
  })

  const filteredData = useMemo(() => {
    const rows = data || []
    if (activeFilter === 'all') return rows
    const wanted = activeFilter === 'active'
    return rows.filter((row) => Boolean(row.record?.is_active) === wanted)
  }, [data, activeFilter])

  return (
    <Box p={4}>
      {isLoading && !data ? (
        <Center h="60vh">
          <Spinner size="xl" color="blue.500" />
        </Center>
      ) : (
        <>
          <Flex
            align="center"
            justify="space-between"
            gap={4}
            mb={6}
            flexWrap="wrap"
          >
            <Heading size="lg">
              {capitalize(recordType)} Records
            </Heading>

            <HStack
              spacing={2}
              bg={filterBg}
              border="1px"
              borderColor={filterBorderColor}
              borderRadius="lg"
              boxShadow="sm"
              px={3}
              py={2}
            >
              <Text fontSize="sm" fontWeight="semibold" color={filterLabelColor}>
                Filter
              </Text>
              {(['active', 'inactive', 'all'] as const).map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={activeFilter === option ? 'solid' : 'ghost'}
                  colorScheme={activeFilter === option ? 'blue' : 'gray'}
                  onClick={() => setActiveFilter(option)}
                  textTransform="capitalize"
                >
                  {option}
                </Button>
              ))}
            </HStack>
          </Flex>

          <DataTable
            showUserId
            data={filteredData}
            isLoading={isLoading}
            error={error}
            // @ts-ignore
            recordType={recordType}
          />
        </>
      )}
    </Box>
  )
}

export default DataPage
